import React, { useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { equipmentService } from '../../../../shared/services/equipments/equipmentsServices';
import { userService } from '../../../../shared/services/users/usersServices';
import { DURATION_OPTIONS } from '../../constants/lockerReservationConstants';
import { userSituation } from '../../../../shared/utils/userSituation';

interface LockerCreateReservationProps {
  mode: 'create' | 'edit';
  isSubmitting: boolean;
  errors: any;
  touched: any;
}

export function LockerCreateReservation({ 
  mode, 
  isSubmitting, 
  errors, 
  touched 
}: LockerCreateReservationProps) {
  const theme = useTheme();
  const { values, setFieldValue } = useFormikContext<any>();

  // Buscar equipamentos
  const { data: equipments = [], isLoading: loadingEquipments } = useQuery({
    queryKey: ['equipments'],
    queryFn: async () => {
      const response = await equipmentService.list();
      return response.data;
    }
  });

  // Buscar usuários
  const { data: users = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await userService.list();
      return response.data;
    }
  });

  const handleDurationChange = (_: Event, newValue: number | number[]) => {
    setFieldValue('durationHours', newValue);
  };

  const formatDurationLabel = (value: number) => {
    return `${value} hora${value > 1 ? 's' : ''}`;
  };

  if (loadingEquipments || loadingUsers) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Grid item xs={12}>
        <Typography fontWeight={600} fontSize={16} color={theme.palette.text.primary} mb={2}>
          Dados da Reserva
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!(touched.equipmentId && errors.equipmentId)}>
          <InputLabel>Equipamento *</InputLabel>
          <Field
            as={Select}
            name="equipmentId"
            label="Equipamento *"
            disabled={isSubmitting}
          >
            {equipments.map((equipment: any) => (
              <MenuItem key={equipment.id} value={equipment.id}>
                {equipment.name}
              </MenuItem>
            ))}
          </Field>
          {touched.equipmentId && errors.equipmentId && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {errors.equipmentId}
            </Typography>
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!(touched.userId && errors.userId)}>
          <InputLabel>Usuário *</InputLabel>
          <Field
            as={Select}
            name="userId"
            label="Usuário *"
            disabled={isSubmitting}
          >
            {users
              .filter((user: any) => userSituation(user) === 'COMPLETO')
              .map((user: any) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
          </Field>
          {touched.userId && errors.userId && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {errors.userId}
            </Typography>
          )}
        </FormControl>
      </Grid>


      <Grid item xs={12}>
        <Box sx={{ mt: 2 }}>
          <Typography gutterBottom fontWeight={500} color={theme.palette.text.primary}>
            Duração da Reserva *
          </Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={values.durationHours}
              onChange={handleDurationChange}
              min={1}
              max={24}
              step={1}
              marks={DURATION_OPTIONS.map(option => ({
                value: option.value,
                label: option.label
              }))}
              valueLabelDisplay="auto"
              valueLabelFormat={formatDurationLabel}
              disabled={isSubmitting}
              sx={{
                '& .MuiSlider-markLabel': {
                  fontSize: '0.75rem',
                }
              }}
            />
          </Box>
          <Typography variant="body2" color={theme.palette.text.secondary} sx={{ mt: 1 }}>
            Duração selecionada: {values.durationHours} hora{values.durationHours > 1 ? 's' : ''}
          </Typography>
          {touched.durationHours && errors.durationHours && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errors.durationHours}
            </Alert>
          )}
        </Box>
      </Grid>

      {values.equipmentId && values.startDate && values.startTime && values.durationHours && (
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Resumo da Reserva:</strong><br />
              Equipamento: {equipments.find((e: any) => e.id === values.equipmentId)?.name}<br />
              Usuário: {users.find((u: any) => u.id === values.userId)?.name}<br />
              Data: {values.startDate} às {values.startTime}<br />
              Duração: {values.durationHours} hora{values.durationHours > 1 ? 's' : ''}
            </Typography>
          </Alert>
        </Grid>
      )}
    </>
  );
} 